/**
 * Centralisation des 10 emails transactionnels d'Emplois BNSSA & MNS.
 *
 * Les 10 emails :
 *
 *   ── Du candidat au recruteur ──
 *   1. emailRecruiter_NewAEApplication      candidat AE postule à une offre
 *   2. emailRecruiter_NewStandardApplication candidat postule à une offre non-AE (CDI, CDD, etc.)
 *
 *   ── Du recruteur au candidat ──
 *   3. emailCandidate_AEAccepted            recruteur retient le candidat AE
 *   4. emailCandidate_AERejected            recruteur refuse le candidat AE
 *   5. emailCandidate_AEUnderReview         recruteur examine la proposition AE
 *   6. emailCandidate_StandardUnderReview   recruteur examine la candidature non-AE
 *
 *   ── De l'admin (système) au candidat ──
 *   7. emailCandidate_AccountCreated        compte candidat créé
 *   8. emailCandidate_DossierPending        dossier AE en attente de validation
 *   9. emailCandidate_DossierRejected       dossier AE refusé (avec motif)
 *  10. emailCandidate_DossierApproved       dossier AE validé
 *
 * Chaque helper est best-effort : il log mais ne lève pas, pour ne jamais bloquer
 * le flux applicatif (inscription, candidature, action recruteur, action admin).
 */

import { lumi } from '@/lib/lumi'

// ─── Helpers de mise en page ─────────────────────────────────────────────────

const BRAND_NAME = 'Emplois BNSSA & MNS'
const BRAND_URL = 'https://bnssa-mns.fr'
// Adresse d'expéditeur officielle utilisée pour TOUS les emails automatiques
// (inscriptions, candidatures, validations/refus de dossiers et d'annonces, etc.).
//
// L'envoi est désormais routé via la Deno Function `sendEmail` (Resend) qui
// positionne réellement le `From:` à `noreply@bnssa-mns.fr` (DKIM/SPF du domaine
// `bnssa-mns.fr` côté Resend). Cette même adresse est aussi utilisée en
// `replyTo` pour que toute réponse arrive bien dessus.
const BRAND_REPLY_TO = 'noreply@bnssa-mns.fr'

// Nom de la Deno Function `sendEmail` qui appelle l'API Resend.
// Voir functions/sendEmail/index.ts. On utilise `lumi.functions.invoke` plutôt
// que `fetch` brut pour bénéficier automatiquement de l'authentification Lumi
// (Bearer token), du bon base URL, et des erreurs structurées.
const SEND_EMAIL_FUNCTION_NAME = 'sendEmail'
const RED = '#dc2626'
const BLUE = '#2563eb'

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Wrapper HTML commun (header dégradé rouge→bleu + footer signature).
 * `gradient` : 'mixed' (par défaut), 'red' (BNSSA / refus), 'blue' (MNS / succès).
 */
function htmlShell(opts: {
  preheader?: string
  badge: string
  title: string
  bodyHtml: string
  ctaLabel?: string
  ctaUrl?: string
  gradient?: 'mixed' | 'red' | 'blue' | 'green'
  footerNote?: string
}): string {
  const gradient =
    opts.gradient === 'red'
      ? `linear-gradient(135deg,${RED} 0%,#7f1d1d 100%)`
      : opts.gradient === 'blue'
        ? `linear-gradient(135deg,${BLUE} 0%,#1e3a8a 100%)`
        : opts.gradient === 'green'
          ? 'linear-gradient(135deg,#059669 0%,#065f46 100%)'
          : `linear-gradient(135deg,${RED} 0%,${BLUE} 100%)`

  const cta =
    opts.ctaLabel && opts.ctaUrl
      ? `<a href="${opts.ctaUrl}" style="display:inline-block;background:${BLUE};color:#ffffff;font-weight:700;font-size:14px;padding:12px 28px;border-radius:8px;text-decoration:none;margin-top:8px;">${escapeHtml(opts.ctaLabel)}</a>`
      : ''

  const preheader = opts.preheader
    ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;font-size:1px;line-height:1px;">${escapeHtml(opts.preheader)}</div>`
    : ''

  return `<!DOCTYPE html>
<html lang="fr">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;">
${preheader}
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;padding:40px 16px;">
  <tr><td align="center">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.08);max-width:600px;">
      <tr>
        <td style="background:${gradient};padding:32px 40px;">
          <p style="margin:0;font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.78);">${BRAND_NAME}</p>
          <p style="margin:6px 0 0;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,.6);">${escapeHtml(opts.badge)}</p>
          <h1 style="margin:10px 0 0;font-size:24px;font-weight:800;color:#ffffff;line-height:1.25;">${escapeHtml(opts.title)}</h1>
        </td>
      </tr>
      <tr>
        <td style="padding:32px 40px;">
          ${opts.bodyHtml}
          ${cta}
        </td>
      </tr>
      <tr>
        <td style="padding:18px 40px;border-top:1px solid #f3f4f6;background:#fafafa;">
          ${opts.footerNote ? `<p style="margin:0 0 8px;font-size:12px;color:#6b7280;line-height:1.5;">${opts.footerNote}</p>` : ''}
          <p style="margin:0;font-size:11px;color:#9ca3af;">
            Email automatique — <a href="${BRAND_URL}" style="color:#9ca3af;text-decoration:underline;">${BRAND_NAME}</a>. Merci de ne pas répondre directement à ce message.
          </p>
        </td>
      </tr>
    </table>
  </td></tr>
</table>
</body>
</html>`
}

/** Petit "récap" tabulaire (label / valeur) injectable dans le body. */
function recapBlock(rows: { label: string; value: string }[]): string {
  return `
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:16px;margin:16px 0 20px;">
  <tr><td>
    <p style="margin:0 0 10px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#9ca3af;">Récapitulatif</p>
    <table width="100%" cellpadding="0" cellspacing="0">
      ${rows
        .map(
          (r) => `<tr>
        <td style="padding:5px 0;color:#6b7280;font-size:13px;width:140px;">${escapeHtml(r.label)}</td>
        <td style="padding:5px 0;font-weight:600;font-size:13px;color:#111827;">${escapeHtml(r.value)}</td>
      </tr>`,
        )
        .join('')}
    </table>
  </td></tr>
</table>`
}

// ─── Labels contrats ─────────────────────────────────────────────────────────

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  'auto-entrepreneur': 'Auto-entrepreneur',
  interim: 'Intérim',
  CDD: 'CDD',
  CDI: 'CDI',
  'temps-partiel-CDD': 'Temps partiel CDD',
  'temps-partiel-CDI': 'Temps partiel CDI',
  stage: 'Stage',
  contractuel: 'Contractuel',
  titulaire: 'Titulaire FPT',
}

export function contractLabel(contractType: string): string {
  return CONTRACT_TYPE_LABELS[contractType] ?? contractType
}

// ─── Send wrapper (best-effort) ──────────────────────────────────────────────

async function sendEmail(opts: {
  to: string
  subject: string
  html: string
  logTag: string
}): Promise<void> {
  if (!opts.to) {
    console.warn(`[email:${opts.logTag}] Skipped — destinataire manquant`)
    return
  }

  // Envoi via la Deno Function `sendEmail` → Resend → From: noreply@bnssa-mns.fr
  // On utilise `lumi.functions.invoke` (et non `fetch` brut) pour que le SDK
  // injecte automatiquement le header Authorization Bearer attendu par
  // l'infra Lumi. Sans cet header, l'appel renvoyait 401 et tombait
  // silencieusement sur le fallback Lumi (d'où l'expéditeur noreply@lumi.new).
  try {
    const result = (await lumi.functions.invoke(SEND_EMAIL_FUNCTION_NAME, {
      method: 'POST',
      body: {
        to: opts.to,
        subject: opts.subject,
        html: opts.html,
        fromName: BRAND_NAME,
        replyTo: BRAND_REPLY_TO,
      },
    })) as { success?: boolean; emailId?: string; error?: string } | null

    if (!result || result.success !== true) {
      console.warn(
        `[email:${opts.logTag}] Réponse inattendue de la Deno Function — fallback Lumi`,
        result,
      )
      await sendViaLumiFallback(opts)
      return
    }

    console.log(
      `[email:${opts.logTag}] Envoyé via Resend à`,
      opts.to,
      result.emailId ? `(id: ${result.emailId})` : '',
      `(from: noreply@bnssa-mns.fr)`,
    )
  } catch (err: unknown) {
    // L'invoke a échoué (réseau, 4xx/5xx, Resend KO, domaine non vérifié,
    // clé invalide…) → on log avec un maximum de détails et on retombe sur
    // le canal historique Lumi pour ne jamais bloquer un flux utilisateur.
    const errAny = err as { data?: unknown; message?: string } | undefined
    console.warn(
      `[email:${opts.logTag}] Échec Deno sendEmail — fallback sur lumi.tools.email.send`,
      errAny?.data ?? errAny?.message ?? err,
    )
    await sendViaLumiFallback(opts)
  }
}

async function sendViaLumiFallback(opts: {
  to: string
  subject: string
  html: string
  logTag: string
}): Promise<void> {
  try {
    await lumi.tools.email.send({
      to: opts.to,
      subject: opts.subject,
      fromName: BRAND_NAME,
      replyTo: BRAND_REPLY_TO,
      html: opts.html,
    })
    console.log(`[email:${opts.logTag}] Fallback Lumi envoyé à`, opts.to)
  } catch (fallbackErr) {
    console.warn(`[email:${opts.logTag}] Fallback Lumi en échec :`, fallbackErr)
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//                              LES 10 EMAILS
// ═══════════════════════════════════════════════════════════════════════════

// ─── 1. Candidat AE → Recruteur : nouvelle candidature AE ────────────────────

export async function emailRecruiter_NewAEApplication(opts: {
  recruiterEmail: string
  recruiterName?: string
  candidateName: string
  candidateEmail: string
  candidatePhone?: string
  jobTitle: string
  jobEmployer: string
  proposedRate?: number
}): Promise<void> {
  const rows = [
    { label: 'Candidat', value: opts.candidateName },
    { label: 'Email', value: opts.candidateEmail },
    ...(opts.candidatePhone ? [{ label: 'Téléphone', value: opts.candidatePhone }] : []),
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Statut', value: 'Auto-entrepreneur' },
    ...(typeof opts.proposedRate === 'number'
      ? [{ label: 'Tarif proposé', value: `${opts.proposedRate} €/h` }]
      : []),
  ]
  const html = htmlShell({
    preheader: `Nouvelle candidature auto-entrepreneur sur "${opts.jobTitle}"`,
    badge: 'Nouvelle candidature AE',
    title: 'Une candidature auto-entrepreneur vient d\'arriver',
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.recruiterName ? ` <strong>${escapeHtml(opts.recruiterName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Un candidat <strong>auto-entrepreneur</strong> vient de postuler à votre offre. Consultez son dossier dans votre back-office, le statut de son dossier professionnel est visible directement depuis le tableau des candidatures.
</p>
${recapBlock(rows)}
<p style="margin:0 0 8px;font-size:13px;color:#6b7280;line-height:1.6;">
  💡 <strong>Bon à savoir :</strong> seuls les candidats AE dont le dossier a été <em>validé par notre équipe</em> peuvent postuler. Vous pouvez voir le statut du dossier (en attente / validé) dans votre tableau de bord.
</p>`,
    ctaLabel: 'Voir la candidature',
    ctaUrl: `${BRAND_URL}/backoffice`,
    footerNote: 'Vous recevez cet email car une candidature a été déposée sur l\'une de vos offres publiées.',
  })

  await sendEmail({
    to: opts.recruiterEmail,
    subject: `🟢 Nouvelle candidature AE — ${opts.jobTitle}`,
    html,
    logTag: 'recruiter:new-ae',
  })
}

// ─── 2. Candidat → Recruteur : nouvelle candidature standard (non-AE) ────────

export async function emailRecruiter_NewStandardApplication(opts: {
  recruiterEmail: string
  recruiterName?: string
  candidateName: string
  candidateEmail: string
  candidatePhone?: string
  jobTitle: string
  jobEmployer: string
  contractType: string
}): Promise<void> {
  const rows = [
    { label: 'Candidat', value: opts.candidateName },
    { label: 'Email', value: opts.candidateEmail },
    ...(opts.candidatePhone ? [{ label: 'Téléphone', value: opts.candidatePhone }] : []),
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Contrat', value: contractLabel(opts.contractType) },
  ]
  const html = htmlShell({
    preheader: `Nouvelle candidature ${contractLabel(opts.contractType)} sur "${opts.jobTitle}"`,
    badge: 'Nouvelle candidature',
    title: `Nouvelle candidature ${contractLabel(opts.contractType)}`,
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.recruiterName ? ` <strong>${escapeHtml(opts.recruiterName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Vous avez reçu une nouvelle candidature pour votre offre <strong>${escapeHtml(opts.jobTitle)}</strong>. Consultez le profil et le CV depuis votre back-office.
</p>
${recapBlock(rows)}`,
    ctaLabel: 'Consulter dans le back-office',
    ctaUrl: `${BRAND_URL}/backoffice`,
    footerNote: 'Vous recevez cet email car une candidature a été déposée sur l\'une de vos offres.',
  })

  await sendEmail({
    to: opts.recruiterEmail,
    subject: `📩 Nouvelle candidature — ${opts.jobTitle}`,
    html,
    logTag: 'recruiter:new-standard',
  })
}

// ─── 3. Recruteur → Candidat AE : candidat retenu ────────────────────────────

export async function emailCandidate_AEAccepted(opts: {
  candidateEmail: string
  candidateName: string
  jobTitle: string
  jobEmployer: string
  recruiterContact?: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Statut', value: 'Retenu ✓' },
  ]
  const html = htmlShell({
    preheader: `Bonne nouvelle : vous êtes retenu(e) sur "${opts.jobTitle}"`,
    badge: 'Candidature retenue',
    title: 'Félicitations, votre candidature est retenue !',
    gradient: 'green',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour <strong>${escapeHtml(opts.candidateName)}</strong>,</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Excellente nouvelle ! Le recruteur de <strong>${escapeHtml(opts.jobEmployer)}</strong> a retenu votre proposition d'auto-entrepreneur sur l'offre <strong>${escapeHtml(opts.jobTitle)}</strong>.
</p>
${recapBlock(rows)}
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  ${opts.recruiterContact
    ? `Le recruteur reviendra vers vous très prochainement à l'adresse <strong>${escapeHtml(opts.recruiterContact)}</strong> pour finaliser les modalités de la mission.`
    : `Le recruteur reviendra vers vous très prochainement pour finaliser les modalités de la mission.`}
</p>`,
    ctaLabel: 'Accéder à mon tableau de bord',
    ctaUrl: `${BRAND_URL}/dashboard`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `🎉 Votre candidature AE est retenue — ${opts.jobTitle}`,
    html,
    logTag: 'candidate:ae-accepted',
  })
}

// ─── 4. Recruteur → Candidat AE : candidat refusé ────────────────────────────

export async function emailCandidate_AERejected(opts: {
  candidateEmail: string
  candidateName: string
  jobTitle: string
  jobEmployer: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Statut', value: 'Non retenu' },
  ]
  const html = htmlShell({
    preheader: `Décision sur votre candidature AE pour "${opts.jobTitle}"`,
    badge: 'Décision recruteur',
    title: 'Suite à donner à votre candidature AE',
    gradient: 'red',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour <strong>${escapeHtml(opts.candidateName)}</strong>,</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Nous vous remercions pour votre proposition d'auto-entrepreneur sur l'offre <strong>${escapeHtml(opts.jobTitle)}</strong> chez <strong>${escapeHtml(opts.jobEmployer)}</strong>.
</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Après examen, le recruteur a décidé de ne pas donner suite à votre candidature pour cette mission précise.
</p>
${recapBlock(rows)}
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Cela ne remet absolument pas en cause votre profil. De nouvelles offres BNSSA et MNS sont publiées chaque semaine, et votre dossier professionnel reste actif sur votre espace candidat.
</p>`,
    ctaLabel: 'Découvrir d\'autres offres',
    ctaUrl: `${BRAND_URL}/offre-emploi`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `Candidature AE non retenue — ${opts.jobTitle}`,
    html,
    logTag: 'candidate:ae-rejected',
  })
}

// ─── 5. Recruteur → Candidat AE : proposition à l'examen ─────────────────────

export async function emailCandidate_AEUnderReview(opts: {
  candidateEmail: string
  candidateName: string
  jobTitle: string
  jobEmployer: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Statut', value: 'En examen' },
  ]
  const html = htmlShell({
    preheader: `Le recruteur examine votre proposition AE pour "${opts.jobTitle}"`,
    badge: 'Examen de votre proposition',
    title: 'Votre proposition AE est en cours d\'examen',
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour <strong>${escapeHtml(opts.candidateName)}</strong>,</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Bonne nouvelle : <strong>${escapeHtml(opts.jobEmployer)}</strong> est en train d'examiner attentivement votre proposition d'auto-entrepreneur sur l'offre <strong>${escapeHtml(opts.jobTitle)}</strong>.
</p>
${recapBlock(rows)}
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Le recruteur reviendra vers vous prochainement avec sa décision (acceptation, demande d'informations complémentaires ou refus). Vous pouvez suivre l'avancement à tout moment depuis votre tableau de bord.
</p>`,
    ctaLabel: 'Suivre ma candidature',
    ctaUrl: `${BRAND_URL}/dashboard`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `🔍 Proposition AE en examen — ${opts.jobTitle}`,
    html,
    logTag: 'candidate:ae-review',
  })
}

// ─── 6. Recruteur → Candidat (non-AE) : candidature à l'examen ───────────────

export async function emailCandidate_StandardUnderReview(opts: {
  candidateEmail: string
  candidateName: string
  jobTitle: string
  jobEmployer: string
  contractType: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Contrat', value: contractLabel(opts.contractType) },
    { label: 'Statut', value: 'En examen' },
  ]
  const html = htmlShell({
    preheader: `Le recruteur examine votre candidature pour "${opts.jobTitle}"`,
    badge: 'Examen de votre candidature',
    title: 'Votre candidature est à l\'examen',
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour <strong>${escapeHtml(opts.candidateName)}</strong>,</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  <strong>${escapeHtml(opts.jobEmployer)}</strong> a pris connaissance de votre candidature pour l'offre <strong>${escapeHtml(opts.jobTitle)}</strong> et est en train de l'examiner.
</p>
${recapBlock(rows)}
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Le recruteur reviendra vers vous très prochainement. Suivez l'évolution de votre candidature directement depuis votre espace candidat.
</p>`,
    ctaLabel: 'Voir mon tableau de bord',
    ctaUrl: `${BRAND_URL}/dashboard`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `🔍 Candidature en examen — ${opts.jobTitle}`,
    html,
    logTag: 'candidate:standard-review',
  })
}

// ─── 7. Admin → Candidat : compte créé ───────────────────────────────────────

export async function emailCandidate_AccountCreated(opts: {
  candidateEmail: string
  candidateName?: string
  role?: 'candidate' | 'recruiter' | 'organisme'
}): Promise<void> {
  const role = opts.role ?? 'candidate'
  const roleLabel =
    role === 'recruiter' ? 'Recruteur' : role === 'organisme' ? 'Organisme de formation' : 'Candidat'
  const dashUrl =
    role === 'recruiter'
      ? `${BRAND_URL}/backoffice`
      : role === 'organisme'
        ? `${BRAND_URL}/dashboard-organisme`
        : `${BRAND_URL}/dashboard`

  const html = htmlShell({
    preheader: `Bienvenue sur ${BRAND_NAME} — votre compte ${roleLabel.toLowerCase()} est actif`,
    badge: 'Bienvenue !',
    title: `Bienvenue sur ${BRAND_NAME} 🎉`,
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.candidateName ? ` <strong>${escapeHtml(opts.candidateName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Votre compte <strong>${roleLabel}</strong> sur <strong>${BRAND_NAME}</strong> a bien été créé. Vous pouvez dès à présent accéder à votre espace personnel et profiter de toutes les fonctionnalités de la plateforme.
</p>
<ul style="margin:0 0 16px;padding-left:20px;color:#374151;font-size:14px;line-height:1.7;">
  ${
    role === 'candidate'
      ? `<li>Postuler en un clic à toutes les offres BNSSA et MNS</li>
         <li>Gérer votre dossier professionnel auto-entrepreneur</li>
         <li>Suivre l'avancement de vos candidatures</li>`
      : role === 'recruiter'
        ? `<li>Publier vos offres d'emploi BNSSA et MNS</li>
           <li>Recevoir et gérer les candidatures depuis votre back-office</li>
           <li>Visualiser le statut des dossiers AE des candidats</li>`
        : `<li>Publier vos sessions de formation BNSSA, MNS, BPJEPS AAN…</li>
           <li>Recevoir des inscriptions directes par vos coordonnées</li>
           <li>Bénéficier d'une visibilité nationale</li>`
  }
</ul>
<p style="margin:0 0 14px;font-size:13px;color:#6b7280;line-height:1.6;">
  Pour toute question, vous pouvez nous écrire directement depuis le site.
</p>`,
    ctaLabel: 'Accéder à mon espace',
    ctaUrl: dashUrl,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `🎉 Bienvenue sur ${BRAND_NAME} — votre compte est actif`,
    html,
    logTag: 'candidate:account-created',
  })
}

// ─── 8. Admin → Candidat : dossier AE en attente ─────────────────────────────

export async function emailCandidate_DossierPending(opts: {
  candidateEmail: string
  candidateName?: string
  category: 'BNSSA' | 'MNS'
}): Promise<void> {
  const html = htmlShell({
    preheader: `Votre dossier AE ${opts.category} a bien été soumis et est en attente de validation`,
    badge: 'Dossier reçu',
    title: 'Votre dossier AE est en cours de validation',
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.candidateName ? ` <strong>${escapeHtml(opts.candidateName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Nous avons bien reçu votre dossier professionnel <strong>auto-entrepreneur ${opts.category}</strong>. Notre équipe va vérifier vos pièces (carte pro, assurance, diplômes, recyclages…) dans les meilleurs délais.
</p>
<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:14px;margin:16px 0 20px;">
  <p style="margin:0;font-size:13px;color:#92400e;line-height:1.6;">
    <strong>⏱️ Délai indicatif :</strong> 24 à 72h ouvrées. Vous recevrez un email dès que la décision est prise (validation ou demande de mise à jour).
  </p>
</div>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  ⚠️ Pendant l'examen, vous ne pouvez pas postuler à de nouvelles offres <strong>auto-entrepreneur</strong>, mais vous pouvez postuler librement aux autres types de contrat (CDI, CDD, intérim, contractuel, titulaire FPT…).
</p>`,
    ctaLabel: 'Voir mon dossier',
    ctaUrl: `${BRAND_URL}/dashboard`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `📁 Votre dossier AE ${opts.category} est en cours de validation`,
    html,
    logTag: 'candidate:dossier-pending',
  })
}

// ─── 9. Admin → Candidat : dossier AE refusé (avec motif) ────────────────────

export async function emailCandidate_DossierRejected(opts: {
  candidateEmail: string
  candidateName?: string
  category: 'BNSSA' | 'MNS'
  rejectionReason?: string
}): Promise<void> {
  const reasonBlock = opts.rejectionReason
    ? `<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:14px;margin:16px 0 20px;">
         <p style="margin:0 0 6px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#991b1b;">Motif du refus</p>
         <p style="margin:0;font-size:14px;color:#7f1d1d;line-height:1.6;">${escapeHtml(opts.rejectionReason)}</p>
       </div>`
    : ''

  const html = htmlShell({
    preheader: `Votre dossier AE ${opts.category} doit être mis à jour`,
    badge: 'Dossier refusé',
    title: 'Votre dossier professionnel doit être mis à jour',
    gradient: 'red',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.candidateName ? ` <strong>${escapeHtml(opts.candidateName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Après examen par notre équipe, votre dossier professionnel <strong>auto-entrepreneur ${opts.category}</strong> a été <strong style="color:${RED};">refusé</strong>.
</p>
${reasonBlock}
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Vous pouvez mettre à jour vos pièces directement depuis votre tableau de bord candidat. Une fois corrigé, votre dossier sera automatiquement soumis à nouveau pour validation.
</p>
<p style="margin:0 0 14px;font-size:13px;color:#6b7280;line-height:1.6;">
  💡 <strong>Bon à savoir :</strong> ce refus de dossier AE ne vous empêche pas de postuler aux offres CDD, CDI, intérim, contractuel, titulaire FPT et autres.
</p>`,
    ctaLabel: 'Mettre mon dossier à jour',
    ctaUrl: `${BRAND_URL}/dashboard`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `⚠️ Dossier AE ${opts.category} refusé — Action requise`,
    html,
    logTag: 'candidate:dossier-rejected',
  })
}

// ─── 10. Admin → Candidat : dossier AE validé ────────────────────────────────

export async function emailCandidate_DossierApproved(opts: {
  candidateEmail: string
  candidateName?: string
  category: 'BNSSA' | 'MNS'
}): Promise<void> {
  const html = htmlShell({
    preheader: `Votre dossier AE ${opts.category} est validé — vous pouvez postuler !`,
    badge: 'Dossier validé',
    title: `Votre dossier professionnel ${opts.category} est validé ✓`,
    gradient: 'green',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.candidateName ? ` <strong>${escapeHtml(opts.candidateName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Excellente nouvelle ! Votre dossier professionnel <strong>auto-entrepreneur ${opts.category}</strong> a été <strong style="color:#059669;">validé</strong> par notre équipe.
</p>
<div style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:8px;padding:14px;margin:16px 0 20px;">
  <p style="margin:0;font-size:14px;color:#065f46;line-height:1.6;">
    🎉 Vous pouvez désormais postuler librement à <strong>toutes les offres auto-entrepreneur</strong> de la plateforme avec votre tarif horaire.
  </p>
</div>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Pensez à mettre à jour vos pièces si l'une d'elles arrive à expiration (recyclage PSE, FC BNSSA…).
</p>`,
    ctaLabel: 'Découvrir les offres AE',
    ctaUrl: `${BRAND_URL}/offre-emploi`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `✅ Dossier AE ${opts.category} validé — Vous pouvez postuler !`,
    html,
    logTag: 'candidate:dossier-approved',
  })
}


// ─── 11. Recruteur → Candidat (non-AE) : candidat retenu ─────────────────────

export async function emailCandidate_StandardAccepted(opts: {
  candidateEmail: string
  candidateName: string
  jobTitle: string
  jobEmployer: string
  contractType: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Contrat', value: contractLabel(opts.contractType) },
    { label: 'Statut', value: 'Retenu ✓' },
  ]
  const html = htmlShell({
    preheader: `Bonne nouvelle : vous êtes retenu(e) sur "${opts.jobTitle}"`,
    badge: 'Candidature retenue',
    title: 'Félicitations, votre candidature est retenue !',
    gradient: 'green',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour <strong>${escapeHtml(opts.candidateName)}</strong>,</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Excellente nouvelle ! <strong>${escapeHtml(opts.jobEmployer)}</strong> a retenu votre candidature pour l'offre <strong>${escapeHtml(opts.jobTitle)}</strong> en <strong>${escapeHtml(contractLabel(opts.contractType))}</strong>.
</p>
${recapBlock(rows)}
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Le recruteur reviendra vers vous très prochainement pour les prochaines étapes (entretien, contrat, prise de poste). Pensez à consulter régulièrement vos emails et votre tableau de bord.
</p>`,
    ctaLabel: 'Accéder à mon tableau de bord',
    ctaUrl: `${BRAND_URL}/dashboard`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `🎉 Votre candidature est retenue — ${opts.jobTitle}`,
    html,
    logTag: 'candidate:standard-accepted',
  })
}

// ─── 12. Recruteur → Candidat (non-AE) : candidat refusé ─────────────────────

export async function emailCandidate_StandardRejected(opts: {
  candidateEmail: string
  candidateName: string
  jobTitle: string
  jobEmployer: string
  contractType: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Contrat', value: contractLabel(opts.contractType) },
    { label: 'Statut', value: 'Non retenu' },
  ]
  const html = htmlShell({
    preheader: `Décision sur votre candidature pour "${opts.jobTitle}"`,
    badge: 'Décision recruteur',
    title: 'Suite à donner à votre candidature',
    gradient: 'red',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour <strong>${escapeHtml(opts.candidateName)}</strong>,</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Nous vous remercions pour votre candidature sur l'offre <strong>${escapeHtml(opts.jobTitle)}</strong> chez <strong>${escapeHtml(opts.jobEmployer)}</strong>.
</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Après examen, le recruteur a décidé de ne pas donner suite à votre candidature pour ce poste précis.
</p>
${recapBlock(rows)}
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Cela ne remet absolument pas en cause votre profil. De nouvelles offres BNSSA et MNS sont publiées chaque semaine — nous vous invitons à continuer à postuler.
</p>`,
    ctaLabel: 'Découvrir d\'autres offres',
    ctaUrl: `${BRAND_URL}/offre-emploi`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `Candidature non retenue — ${opts.jobTitle}`,
    html,
    logTag: 'candidate:standard-rejected',
  })
}

// ─── 13. Recruteur → Candidat : invitation entretien ─────────────────────────

export async function emailCandidate_InterviewInvite(opts: {
  candidateEmail: string
  candidateName: string
  jobTitle: string
  jobEmployer: string
  contractType: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Contrat', value: contractLabel(opts.contractType) },
    { label: 'Statut', value: 'Entretien' },
  ]
  const html = htmlShell({
    preheader: `${opts.jobEmployer} souhaite vous rencontrer pour un entretien`,
    badge: 'Entretien proposé',
    title: 'Le recruteur souhaite vous rencontrer 🤝',
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour <strong>${escapeHtml(opts.candidateName)}</strong>,</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Bonne nouvelle ! <strong>${escapeHtml(opts.jobEmployer)}</strong> souhaite organiser un <strong>entretien</strong> avec vous concernant l'offre <strong>${escapeHtml(opts.jobTitle)}</strong>.
</p>
${recapBlock(rows)}
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Le recruteur va prendre contact directement avec vous (par email ou téléphone) pour convenir d'une date et des modalités. Restez attentif(ve) à vos messages dans les prochains jours.
</p>`,
    ctaLabel: 'Voir mon tableau de bord',
    ctaUrl: `${BRAND_URL}/dashboard`,
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `🤝 Entretien proposé — ${opts.jobTitle}`,
    html,
    logTag: 'candidate:interview-invite',
  })
}


// ─── 14. Admin → Recruteur : annonce en attente de validation (juste publiée) ─

export async function emailRecruiter_JobPending(opts: {
  recruiterEmail: string
  recruiterName?: string
  jobTitle: string
  jobEmployer: string
  category: 'BNSSA' | 'MNS'
  contractType: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Catégorie', value: opts.category },
    { label: 'Contrat', value: contractLabel(opts.contractType) },
    { label: 'Statut', value: 'En attente de validation' },
  ]
  const html = htmlShell({
    preheader: `Votre annonce "${opts.jobTitle}" est en cours de validation par notre équipe`,
    badge: 'Annonce reçue',
    title: 'Votre annonce est en cours de validation',
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.recruiterName ? ` <strong>${escapeHtml(opts.recruiterName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Nous avons bien reçu votre annonce <strong>${escapeHtml(opts.jobTitle)}</strong>. Avant sa mise en ligne publique, notre équipe va la vérifier (cohérence du contenu, conformité, qualité) afin de garantir une expérience de qualité aux candidats.
</p>
${recapBlock(rows)}
<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:14px;margin:16px 0 20px;">
  <p style="margin:0;font-size:13px;color:#92400e;line-height:1.6;">
    <strong>⏱️ Délai indicatif :</strong> 24 à 72h ouvrées. Vous recevrez un email automatique dès que la décision est prise (validation ou demande d'ajustement).
  </p>
</div>
<p style="margin:0 0 14px;font-size:13px;color:#6b7280;line-height:1.6;">
  Vous pouvez consulter et modifier votre annonce à tout moment depuis votre back-office recruteur.
</p>`,
    ctaLabel: 'Accéder à mon back-office',
    ctaUrl: `${BRAND_URL}/backoffice`,
    footerNote: 'Vous recevez cet email parce que vous venez de publier une annonce sur Emplois BNSSA & MNS.',
  })

  await sendEmail({
    to: opts.recruiterEmail,
    subject: `📋 Annonce en cours de validation — ${opts.jobTitle}`,
    html,
    logTag: 'recruiter:job-pending',
  })
}

// ─── 15. Admin → Recruteur : annonce validée et publiée ──────────────────────

export async function emailRecruiter_JobApproved(opts: {
  recruiterEmail: string
  recruiterName?: string
  jobTitle: string
  jobEmployer: string
  category: 'BNSSA' | 'MNS'
  contractType: string
  jobId?: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Catégorie', value: opts.category },
    { label: 'Contrat', value: contractLabel(opts.contractType) },
    { label: 'Statut', value: 'Validée et en ligne ✓' },
  ]
  const ctaUrl = opts.jobId ? `${BRAND_URL}/offre/${opts.jobId}` : `${BRAND_URL}/backoffice`
  const html = htmlShell({
    preheader: `Bonne nouvelle : votre annonce "${opts.jobTitle}" est en ligne`,
    badge: 'Annonce validée',
    title: 'Votre annonce est maintenant publiée 🎉',
    gradient: 'green',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.recruiterName ? ` <strong>${escapeHtml(opts.recruiterName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Excellente nouvelle ! Votre annonce <strong>${escapeHtml(opts.jobTitle)}</strong> a été <strong style="color:#059669;">validée par notre équipe</strong> et est désormais visible publiquement par les candidats BNSSA et MNS.
</p>
${recapBlock(rows)}
<div style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:8px;padding:14px;margin:16px 0 20px;">
  <p style="margin:0;font-size:14px;color:#065f46;line-height:1.6;">
    🎯 Les candidats peuvent désormais postuler directement depuis la fiche offre. Vous recevrez un email à chaque nouvelle candidature.
  </p>
</div>
<p style="margin:0 0 14px;font-size:13px;color:#6b7280;line-height:1.6;">
  Pensez à consulter régulièrement votre back-office pour traiter les candidatures rapidement (les meilleurs candidats partent vite !).
</p>`,
    ctaLabel: 'Voir mon annonce en ligne',
    ctaUrl,
  })

  await sendEmail({
    to: opts.recruiterEmail,
    subject: `✅ Annonce validée et publiée — ${opts.jobTitle}`,
    html,
    logTag: 'recruiter:job-approved',
  })
}

// ─── 18. Système → Candidat : alerte emploi (nouvelles offres matchées) ──────

export async function emailCandidate_JobAlertMatches(opts: {
  candidateEmail: string
  candidateName?: string
  alertName: string
  jobs: Array<{
    id: string
    title: string
    employer: string
    location: string
    category: 'BNSSA' | 'MNS'
    contractType: string
  }>
}): Promise<void> {
  if (!opts.jobs || opts.jobs.length === 0) return

  const count = opts.jobs.length
  const jobsHtml = opts.jobs
    .slice(0, 10)
    .map((j) => {
      const catColor = j.category === 'BNSSA' ? RED : BLUE
      const url = `${BRAND_URL}/offres/${escapeHtml(j.id)}`
      return `<tr><td style="padding:14px 0;border-bottom:1px solid #f3f4f6;">
        <p style="margin:0 0 4px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${catColor};">${escapeHtml(j.category)} · ${escapeHtml(contractLabel(j.contractType))}</p>
        <a href="${url}" style="font-size:15px;font-weight:700;color:#111827;text-decoration:none;">${escapeHtml(j.title)}</a>
        <p style="margin:4px 0 0;font-size:13px;color:#6b7280;">${escapeHtml(j.employer)} · ${escapeHtml(j.location)}</p>
        <a href="${url}" style="display:inline-block;margin-top:6px;font-size:12px;font-weight:600;color:${BLUE};text-decoration:none;">Voir l'offre →</a>
      </td></tr>`
    })
    .join('')

  const moreNote =
    count > 10
      ? `<p style="margin:8px 0 0;font-size:12px;color:#6b7280;font-style:italic;">+ ${count - 10} autre${count - 10 > 1 ? 's' : ''} offre${count - 10 > 1 ? 's' : ''} disponible${count - 10 > 1 ? 's' : ''} sur le site.</p>`
      : ''

  const html = htmlShell({
    preheader: `${count} nouvelle${count > 1 ? 's' : ''} offre${count > 1 ? 's' : ''} pour votre alerte « ${opts.alertName} »`,
    badge: 'Alerte emploi',
    title: `${count} nouvelle${count > 1 ? 's' : ''} offre${count > 1 ? 's' : ''} pour vous 🔔`,
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.candidateName ? ` <strong>${escapeHtml(opts.candidateName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Votre alerte <strong>« ${escapeHtml(opts.alertName)} »</strong> a trouvé <strong>${count} nouvelle${count > 1 ? 's' : ''} offre${count > 1 ? 's' : ''}</strong> qui correspond${count > 1 ? 'ent' : ''} à vos critères :
</p>
<table width="100%" cellpadding="0" cellspacing="0" style="margin:8px 0 16px;">
  ${jobsHtml}
</table>
${moreNote}
<p style="margin:18px 0 0;font-size:13px;color:#6b7280;line-height:1.6;">
  💡 Vous pouvez modifier ou désactiver vos alertes à tout moment depuis votre tableau de bord candidat.
</p>`,
    ctaLabel: 'Gérer mes alertes',
    ctaUrl: `${BRAND_URL}/dashboard?tab=alertes`,
    footerNote: "Vous recevez cet email parce que vous avez créé une alerte emploi sur Emplois BNSSA & MNS.",
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: `🔔 ${count} nouvelle${count > 1 ? 's' : ''} offre${count > 1 ? 's' : ''} — Alerte « ${opts.alertName} »`,
    html,
    logTag: 'candidate:job-alert-matches',
  })
}

// ─── 17. Système → Candidat : profil CVthèque expiré (48h) ────────────────────

export async function emailCandidate_AvailabilityExpired(opts: {
  candidateEmail: string
  candidateName?: string
  qualification?: string
  publicId?: string
}): Promise<void> {
  const labelBits = [opts.qualification, opts.publicId].filter(Boolean).map(s => String(s).trim().replace(/\s+/g, '-')).join('-')
  const html = htmlShell({
    preheader: "Votre profil 'Disponible Immédiatement !' a expiré — réactivez-le en 1 clic",
    badge: 'Profil expiré',
    title: "Votre profil n'est plus en ligne dans la CVthèque",
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.candidateName ? ` <strong>${escapeHtml(opts.candidateName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Votre profil <strong>${labelBits ? escapeHtml(labelBits) + ' — ' : ''}« Disponible maintenant ! »</strong> a été automatiquement <strong>retiré de la CVthèque</strong> après 48&nbsp;heures, comme prévu pour garantir aux recruteurs des disponibilités fiables et à jour.
</p>
<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:14px;margin:16px 0 20px;">
  <p style="margin:0;font-size:13px;color:#92400e;line-height:1.6;">
    Toujours disponible ? <strong>Réactivez votre profil en 1 clic</strong> depuis votre espace candidat. Il sera de nouveau visible pendant 48&nbsp;heures par les recruteurs et les piscines.
  </p>
</div>
<p style="margin:0 0 14px;font-size:13px;color:#6b7280;line-height:1.6;">
  Vos informations (téléphone, email, ville, région, mobilité…) sont conservées : aucune ressaisie nécessaire.
</p>`,
    ctaLabel: 'Réactiver mon profil',
    ctaUrl: `${BRAND_URL}/dashboard?tab=disponible`,
    footerNote: 'Vous recevez cet email parce que vous aviez activé le profil « Disponible maintenant ! » sur Emplois BNSSA & MNS.',
  })

  await sendEmail({
    to: opts.candidateEmail,
    subject: "⏱️ Votre profil 'Disponible maintenant !' a expiré — réactivez-le",
    html,
    logTag: 'candidate:availability-expired',
  })
}

// ─── 19. Système → Recruteur : alerte candidat (nouveaux profils dispo immédiat) ─

export async function emailRecruiter_CandidateAlertMatches(opts: {
  recruiterEmail: string
  recruiterName?: string
  alertName: string
  candidates: Array<{
    id: string
    label: string
    qualification: string
    city: string
    region: string
    mobility: string
    housingNeed: string
    availableUntil?: string
  }>
}): Promise<void> {
  if (!opts.candidates || opts.candidates.length === 0) return

  const count = opts.candidates.length
  const candidatesHtml = opts.candidates
    .slice(0, 10)
    .map((c) => {
      const isBlue = /BPJEPS|BEESAN|STAPS|MNS/i.test(c.qualification)
      const catColor = isBlue ? BLUE : RED
      const url = `${BRAND_URL}/disponibles`
      const remaining = c.availableUntil
        ? (() => {
            const ms = new Date(c.availableUntil!).getTime() - Date.now()
            if (ms <= 0) return ''
            const h = Math.floor(ms / 3600000)
            return h >= 24 ? `${Math.floor(h / 24)} j ${h % 24} h` : `${h} h`
          })()
        : ''
      return `<tr><td style="padding:14px 0;border-bottom:1px solid #f3f4f6;">
        <p style="margin:0 0 4px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${catColor};">${escapeHtml(c.qualification)}${remaining ? ` · Encore ${escapeHtml(remaining)}` : ''}</p>
        <a href="${url}" style="font-size:15px;font-weight:700;color:#111827;text-decoration:none;">${escapeHtml(c.label)}</a>
        <p style="margin:4px 0 0;font-size:13px;color:#6b7280;">📍 ${escapeHtml(c.city)} · ${escapeHtml(c.region)} · ${escapeHtml(c.mobility)} · Logement : ${escapeHtml(c.housingNeed)}</p>
        <a href="${url}" style="display:inline-block;margin-top:6px;font-size:12px;font-weight:600;color:${BLUE};text-decoration:none;">Voir le profil →</a>
      </td></tr>`
    })
    .join('')

  const moreNote =
    count > 10
      ? `<p style="margin:8px 0 0;font-size:12px;color:#6b7280;font-style:italic;">+ ${count - 10} autre${count - 10 > 1 ? 's' : ''} candidat${count - 10 > 1 ? 's' : ''} disponible${count - 10 > 1 ? 's' : ''} sur la CVthèque.</p>`
      : ''

  const html = htmlShell({
    preheader: `${count} nouveau${count > 1 ? 'x' : ''} candidat${count > 1 ? 's' : ''} disponible${count > 1 ? 's' : ''} pour votre alerte « ${opts.alertName} »`,
    badge: 'Alerte candidat',
    title: `${count} nouveau${count > 1 ? 'x' : ''} profil${count > 1 ? 's' : ''} disponible${count > 1 ? 's' : ''} 🔔`,
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.recruiterName ? ` <strong>${escapeHtml(opts.recruiterName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Votre alerte candidat <strong>« ${escapeHtml(opts.alertName)} »</strong> a trouvé <strong>${count} nouveau${count > 1 ? 'x' : ''} profil${count > 1 ? 's' : ''}</strong> immédiatement disponible${count > 1 ? 's' : ''} qui correspond${count > 1 ? 'ent' : ''} à vos critères :
</p>
<table width="100%" cellpadding="0" cellspacing="0" style="margin:8px 0 16px;">
  ${candidatesHtml}
</table>
${moreNote}
<p style="margin:18px 0 0;font-size:13px;color:#6b7280;line-height:1.6;">
  💡 Les profils disponibles immédiatement sont en ligne <strong>pendant 48h</strong>. Contactez-les rapidement via la CVthèque pour ne pas passer à côté.
</p>`,
    ctaLabel: 'Voir les candidats disponibles',
    ctaUrl: `${BRAND_URL}/disponibles`,
    footerNote: "Vous recevez cet email parce que vous avez créé une alerte candidat sur Emplois BNSSA & MNS.",
  })

  await sendEmail({
    to: opts.recruiterEmail,
    subject: `🔔 ${count} nouveau${count > 1 ? 'x' : ''} candidat${count > 1 ? 's' : ''} dispo — Alerte « ${opts.alertName} »`,
    html,
    logTag: 'recruiter:candidate-alert-matches',
  })
}

// ─── 16. Admin → Recruteur : annonce refusée (avec motif) ────────────────────

export async function emailRecruiter_JobRejected(opts: {
  recruiterEmail: string
  recruiterName?: string
  jobTitle: string
  jobEmployer: string
  category: 'BNSSA' | 'MNS'
  contractType: string
  rejectionReason?: string
}): Promise<void> {
  const rows = [
    { label: 'Offre', value: opts.jobTitle },
    { label: 'Employeur', value: opts.jobEmployer },
    { label: 'Catégorie', value: opts.category },
    { label: 'Contrat', value: contractLabel(opts.contractType) },
    { label: 'Statut', value: 'Non publiée' },
  ]
  const reasonBlock = opts.rejectionReason
    ? `<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:14px;margin:16px 0 20px;">
         <p style="margin:0 0 6px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#991b1b;">Motif du refus</p>
         <p style="margin:0;font-size:14px;color:#7f1d1d;line-height:1.6;">${escapeHtml(opts.rejectionReason)}</p>
       </div>`
    : ''

  const html = htmlShell({
    preheader: `Votre annonce "${opts.jobTitle}" n'a pas pu être publiée en l'état`,
    badge: 'Annonce non publiée',
    title: 'Votre annonce nécessite des ajustements',
    gradient: 'red',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.recruiterName ? ` <strong>${escapeHtml(opts.recruiterName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Après examen, notre équipe n'a pas pu valider votre annonce <strong>${escapeHtml(opts.jobTitle)}</strong> en l'état. Elle n'est donc pas visible publiquement pour le moment.
</p>
${reasonBlock}
${recapBlock(rows)}
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Vous pouvez modifier l'annonce directement depuis votre back-office. Une fois corrigée, elle sera automatiquement soumise à nouveau pour validation.
</p>
<p style="margin:0 0 14px;font-size:13px;color:#6b7280;line-height:1.6;">
  💡 <strong>Conseils fréquents :</strong> précisez bien le lieu de la mission, le type de contrat, les missions principales, et évitez les contenus discriminatoires ou hors sujet (BNSSA/MNS uniquement).
</p>`,
    ctaLabel: 'Corriger mon annonce',
    ctaUrl: `${BRAND_URL}/backoffice`,
  })

  await sendEmail({
    to: opts.recruiterEmail,
    subject: `⚠️ Annonce non publiée — ${opts.jobTitle}`,
    html,
    logTag: 'recruiter:job-rejected',
  })
}


// ─── 20. Messagerie interne : notification de nouveau message ────────────────

export async function emailUser_NewMessage(opts: {
  recipientEmail: string
  recipientName?: string
  senderDisplayName: string
  senderRole: 'candidate' | 'recruiter' | 'organisme'
  messagePreview: string
  conversationContext?: string
  conversationUrl?: string
}): Promise<void> {
  const roleLabel =
    opts.senderRole === 'recruiter'
      ? 'Un recruteur'
      : opts.senderRole === 'organisme'
        ? 'Un organisme de formation'
        : 'Un candidat'
  const ctaUrl = opts.conversationUrl || `${BRAND_URL}/messages`
  const html = htmlShell({
    preheader: `${opts.senderDisplayName} vous a envoyé un message sur ${BRAND_NAME}`,
    badge: 'Nouveau message',
    title: `${roleLabel} vous a écrit 💬`,
    gradient: 'mixed',
    bodyHtml: `
<p style="margin:0 0 14px;font-size:15px;color:#111827;">Bonjour${opts.recipientName ? ` <strong>${escapeHtml(opts.recipientName)}</strong>` : ''},</p>
<p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.6;">
  Vous avez reçu un nouveau message de <strong>${escapeHtml(opts.senderDisplayName)}</strong>${opts.conversationContext ? ` à propos de <strong>${escapeHtml(opts.conversationContext)}</strong>` : ''}.
</p>
<div style="background:#f9fafb;border-left:3px solid ${BLUE};border-radius:6px;padding:14px 16px;margin:16px 0 20px;">
  <p style="margin:0;font-size:14px;color:#374151;line-height:1.6;font-style:italic;white-space:pre-wrap;">${escapeHtml(opts.messagePreview.slice(0, 300))}${opts.messagePreview.length > 300 ? '…' : ''}</p>
</div>
<p style="margin:0 0 14px;font-size:13px;color:#6b7280;line-height:1.6;">
  💬 Répondez directement depuis votre messagerie pour continuer la conversation.
</p>`,
    ctaLabel: 'Voir le message',
    ctaUrl,
    footerNote: 'Vous recevez cet email car un autre utilisateur vous a contacté via la messagerie interne d\'Emplois BNSSA & MNS.',
  })

  await sendEmail({
    to: opts.recipientEmail,
    subject: `💬 Nouveau message de ${opts.senderDisplayName}`,
    html,
    logTag: 'messaging:new-message',
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Demande de suppression de compte (envoyée à l'admin)
// ─────────────────────────────────────────────────────────────────────────────
export async function emailAdmin_AccountDeletionRequest(opts: {
  userEmail: string
  userId?: string
  reason?: string
  accountType?: string
}) {
  const { userEmail, userId, reason, accountType } = opts
  const submittedAt = new Date().toLocaleString('fr-FR', {
    dateStyle: 'long',
    timeStyle: 'short',
  })

  const html = `
    <div style="font-family:Inter,system-ui,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#111827;">
      <h2 style="margin:0 0 12px;font-size:18px;">Nouvelle demande de suppression de compte</h2>
      <p style="margin:0 0 16px;font-size:14px;line-height:1.5;color:#374151;">
        Un utilisateur a demandé la suppression définitive de son compte sur <strong>Emplois BNSSA &amp; MNS</strong>.
      </p>
      <table style="width:100%;border-collapse:collapse;font-size:14px;margin:0 0 16px;">
        <tbody>
          <tr><td style="padding:6px 0;color:#6b7280;width:140px;">Email</td><td style="padding:6px 0;font-weight:600;">${userEmail}</td></tr>
          ${userId ? `<tr><td style="padding:6px 0;color:#6b7280;">User ID</td><td style="padding:6px 0;font-family:monospace;font-size:12px;">${userId}</td></tr>` : ''}
          ${accountType ? `<tr><td style="padding:6px 0;color:#6b7280;">Type de compte</td><td style="padding:6px 0;">${accountType}</td></tr>` : ''}
          <tr><td style="padding:6px 0;color:#6b7280;">Demandé le</td><td style="padding:6px 0;">${submittedAt}</td></tr>
        </tbody>
      </table>
      ${
        reason
          ? `<div style="background:#f9fafb;border-left:3px solid #dc2626;padding:12px 16px;border-radius:6px;margin:0 0 16px;">
              <p style="margin:0 0 4px;font-size:12px;text-transform:uppercase;letter-spacing:0.04em;color:#6b7280;">Motif indiqué</p>
              <p style="margin:0;font-size:14px;line-height:1.5;white-space:pre-wrap;">${reason.replace(/</g, '&lt;')}</p>
            </div>`
          : ''
      }
      <p style="margin:16px 0 0;font-size:13px;color:#6b7280;line-height:1.5;">
        Action requise : supprimer le compte Lumi associé et purger les données liées (candidatures, dossiers AE, profils CVthèque, alertes, profil entreprise) conformément au RGPD.
      </p>
    </div>
  `

  return sendEmail({
    to: 'noreply@bnssa-mns.fr',
    subject: `🗑️ Demande de suppression de compte — ${userEmail}`,
    html,
  })
}
